
import React from 'react';
import { Journey, Session, KnowledgeDoc } from '@shared/types';

interface Props {
  journeys: Journey[];
  sessions: Session[];
  knowledgeDocs: KnowledgeDoc[];
  onNew: () => void;
  onSelect: (j: Journey) => void;
  onSelectSession: (s: Session) => void;
}

const Dashboard: React.FC<Props> = ({ journeys, sessions, knowledgeDocs, onNew, onSelect, onSelectSession }) => {
  const ongoingSessions = sessions
    .filter(s => !s.isComplete && !s.isPublic)
    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
  
  const completedSessions = sessions
    .filter(s => s.isComplete)
    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

  // Sadece kullanıcının kendi completed sessions'ları için analytics hesapla (public olanları hariç)
  const userCompletedSessions = completedSessions.filter(s => !s.isPublic);

  const totalScores = userCompletedSessions.reduce((acc, s) => {
    if (!s.attempts.length) return acc;
    const sessionAvg = s.attempts.reduce((a, att) => a + (att.feedback?.overallScore || 0), 0) / s.attempts.length;
    return acc + sessionAvg;
  }, 0);

  const avgScore = userCompletedSessions.length > 0 ? Math.round(totalScores / userCompletedSessions.length) : 0;
  const portfolioCount = knowledgeDocs.filter(d => !d.isSystem).length;

  return (
    <div className="p-4 sm:p-8 lg:p-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 sm:mb-12 gap-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">Dashboard</h1>
            <span className="bg-emerald-100 text-emerald-700 text-[8px] sm:text-[9px] font-black px-2 py-0.5 rounded uppercase border border-emerald-200 tracking-widest">Active</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-2 text-slate-500 font-medium italic text-xs sm:text-sm">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
              GMC Standards: Active
            </span>
          </div>
        </div>
        <button 
          onClick={onNew} 
          className="w-full sm:w-auto bg-blue-600 text-white px-8 sm:px-10 py-4 rounded-2xl sm:rounded-[1.75rem] text-sm font-black shadow-xl hover:bg-blue-700 transition-all active:scale-95 flex items-center justify-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Interview
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-12 sm:mb-16">
        <div className="bg-white p-4 sm:p-8 rounded-2xl sm:rounded-[2.5rem] border border-slate-100 shadow-sm">
          <p className="text-slate-400 text-[8px] sm:text-[10px] font-black uppercase tracking-widest mb-1 sm:mb-2">Readiness</p>
          <h3 className="text-2xl sm:text-5xl font-black text-blue-600">{avgScore}%</h3>
        </div>
        <div className="bg-white p-4 sm:p-8 rounded-2xl sm:rounded-[2.5rem] border border-slate-100 shadow-sm">
          <p className="text-slate-400 text-[8px] sm:text-[10px] font-black uppercase tracking-widest mb-1 sm:mb-2">Stations</p>
          <h3 className="text-2xl sm:text-5xl font-black text-slate-800">{sessions.filter(s => !s.isPublic).reduce((acc, s) => acc + s.attempts.length, 0)}</h3>
        </div>
        <div className="bg-white p-4 sm:p-8 rounded-2xl sm:rounded-[2.5rem] border border-slate-100 shadow-sm">
          <p className="text-blue-400 text-[8px] sm:text-[10px] font-black uppercase tracking-widest mb-1 sm:mb-2">Drafts</p>
          <h3 className="text-2xl sm:text-5xl font-black text-slate-800">{ongoingSessions.length}</h3>
        </div>
        <div className="bg-blue-50 p-4 sm:p-8 rounded-2xl sm:rounded-[2.5rem] border border-blue-100 shadow-sm">
          <p className="text-blue-600 text-[8px] sm:text-[10px] font-black uppercase tracking-widest mb-1 sm:mb-2">Portfolio</p>
          <h3 className="text-2xl sm:text-5xl font-black text-blue-700">{portfolioCount}</h3>
        </div>
      </div>

      
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
        <section>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight mb-4 sm:mb-6 flex items-center gap-3">Ongoing Practice</h2>
          {ongoingSessions.length === 0 ? (
            <div className="p-10 text-center bg-slate-50/50 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 text-xs font-bold uppercase">No active drafts.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {ongoingSessions.map(s => {
                const j = journeys.find(jr => jr.id === s.journeyId);
                const progress = j ? Math.round((s.attempts.length / j.questions.length) * 100) : 0;
                const isPublic = s.isPublic === true;
                return (
                  <div key={s.id} onClick={() => onSelectSession(s)} className={`p-6 sm:p-8 bg-white border-2 rounded-2xl sm:rounded-[3rem] shadow-xl cursor-pointer hover:-translate-y-1 transition-all select-none ${isPublic ? 'border-emerald-500' : 'border-blue-500'}`}>
                    <div className="flex justify-between items-start mb-4">
                      <span className={`text-[8px] font-black px-3 py-1 rounded-full uppercase ${isPublic ? 'text-emerald-600 bg-emerald-100' : 'text-blue-600 bg-blue-100'}`}>
                        {isPublic ? 'Public - In Progress' : 'In Progress'}
                      </span>
                      {isPublic && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-emerald-600">
                          <circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/>
                        </svg>
                      )}
                    </div>
                    <h3 className="text-lg font-black text-slate-800 mb-2">{j?.requirements?.roleTitle || j?.specialty || 'General'} Mock</h3>
                    <p className="text-slate-500 text-xs font-medium mb-4">
                      {new Date(s.startTime).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-600 transition-all" style={{ width: `${progress}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
        <section>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight mb-4 sm:mb-6 flex items-center gap-3">Completed Interviews</h2>
          {completedSessions.filter(s => !s.isPublic).length === 0 ? (
            <div className="p-10 text-center bg-slate-50/50 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 text-xs font-bold uppercase">No completed interviews yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {completedSessions.filter(s => !s.isPublic).slice(0, 6).map(s => {
                const j = journeys.find(jr => jr.id === s.journeyId);
                const avgScore = s.attempts.length > 0
                  ? Math.round(s.attempts.reduce((acc, att) => acc + (att.feedback?.overallScore || 0), 0) / s.attempts.length)
                  : 0;
                return (
                  <div key={s.id} onClick={() => onSelectSession(s)} className="p-6 sm:p-8 border-2 bg-emerald-50 border-emerald-500 rounded-2xl sm:rounded-[3rem] shadow-xl cursor-pointer hover:-translate-y-1 transition-all select-none">
                    <div className="flex justify-between items-start mb-4">
                      <span className="text-[8px] font-black px-3 py-1 rounded-full uppercase text-emerald-600 bg-emerald-100">Completed</span>
                      <div className="flex items-center gap-2">
                        <span className="text-emerald-600 font-black text-lg">{avgScore}%</span>
                      </div>
                    </div>
                    <h3 className="text-lg font-black text-slate-800 mb-2">{j?.requirements?.roleTitle || j?.specialty || 'General'} Mock</h3>
                    <p className="text-slate-500 text-xs font-medium">
                      {new Date(s.endTime || s.startTime).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <div className="mt-10 sm:mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
        <section>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight mb-4 sm:mb-6">Mastery Blueprints</h2>

          {/* Your Blueprints */}
          <div>
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 bg-blue-500 rounded"></span>
              Your Blueprints
            </h3>
            {journeys.filter(j => !j.isPublic).length === 0 ? (
              <div className="text-center py-12 bg-slate-50/50 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 font-bold text-sm">Start by creating a track.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {journeys.filter(j => !j.isPublic).map(j => {
                  const isCompleted = completedSessions.some(s => s.journeyId === j.id && !s.isPublic);
                  return (
                    <div
                      key={j.id}
                      onClick={() => onSelect(j)}
                      className={`p-6 sm:p-8 border border-slate-100 rounded-2xl sm:rounded-[3rem] transition-all bg-white hover:shadow-xl hover:border-blue-400 cursor-pointer ${isCompleted ? 'opacity-70' : ''}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <span className="text-[8px] font-black px-2 py-1 rounded-full uppercase bg-slate-50 text-slate-500">{j.grade}</span>
                        {isCompleted && (
                          <span className="text-[8px] font-black px-3 py-1 rounded-full uppercase text-emerald-600 bg-emerald-100">Completed</span>
                        )}
                      </div>
                      <h3 className="text-lg font-black text-slate-800">{j.specialty}</h3>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight mb-4 sm:mb-6">Public Interviews</h2>
          {journeys.filter(j => j.isPublic).length === 0 && completedSessions.filter(s => s.isPublic).length === 0 ? (
            <div className="text-center py-12 bg-slate-50/50 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 font-bold text-sm">No public interviews available.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {/* Public Journeys */}
              {journeys.filter(j => j.isPublic).map(j => (
                <div key={`journey-${j.id}`} onClick={() => onSelect(j)} className="p-6 sm:p-8 border border-emerald-300 rounded-2xl sm:rounded-[3rem] hover:shadow-xl hover:border-emerald-400 cursor-pointer transition-all bg-white">
                  <div className="flex justify-between items-start mb-4">
                    <span className="text-[8px] font-black px-2 py-1 rounded-full uppercase bg-emerald-50 text-emerald-600">{j.grade}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-emerald-600">
                      <circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/>
                    </svg>
                  </div>
                  <h3 className="text-lg font-black text-slate-800">{j.specialty}</h3>
                  <p className="text-emerald-600 text-xs font-medium mt-2">Public Template - View Only</p>
                </div>
              ))}

              {/* Public Completed Sessions */}
              {completedSessions.filter(s => s.isPublic).map(s => {
                const j = journeys.find(jr => jr.id === s.journeyId);
                const avgScore = s.attempts.length > 0
                  ? Math.round(s.attempts.reduce((acc, att) => acc + (att.feedback?.overallScore || 0), 0) / s.attempts.length)
                  : 0;
                return (
                  <div key={`session-${s.id}`} onClick={() => onSelectSession(s)} className="p-6 sm:p-8 border-2 border-emerald-500 rounded-2xl sm:rounded-[3rem] shadow-xl cursor-pointer hover:-translate-y-1 transition-all select-none bg-emerald-50">
                    <div className="flex justify-between items-start mb-4">
                      <span className="text-[8px] font-black px-3 py-1 rounded-full uppercase text-emerald-600 bg-emerald-100">Public - Completed</span>
                      <div className="flex items-center gap-2">
                        <span className="text-emerald-600 font-black text-lg">{avgScore}%</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-emerald-600">
                          <circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/>
                        </svg>
                      </div>
                    </div>
                    <h3 className="text-lg font-black text-slate-800 mb-2">{j?.requirements?.roleTitle || j?.specialty || 'General'} Mock</h3>
                    <p className="text-slate-500 text-xs font-medium">
                      {new Date(s.endTime || s.startTime).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
